import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import { RoutePath } from '@common/enums';
import { AuthBootstrapService } from '@common/services/auth/auth-bootstrap.service';
import { AuthContextService } from '@common/services/auth/auth-context.service';
import { AuthSessionService } from '@common/services/auth/auth-session.service';
import { AuthService } from '@common/services/auth/auth.service';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

const isLoggedIn = (): boolean => {
  const authSession = inject(AuthSessionService);

  if (authSession.loggedIn() == null) {
    inject(AuthService).checkTokenInLocalStorage();
  }

  return authSession.loggedIn();
};

const logoutUnauthenticated = (authService: AuthService): void =>
  authService.logout(
    new HttpErrorResponse({
      status: 440,
      error: { message: 'Access denied. Authentication required' }
    })
  );

// 401 is already turned into a logout by sessionInterceptor, 503 into the Account page by errorHandlerInterceptor
const handleBootstrapError = (authService: AuthService, error: unknown): void => {
  const status = error instanceof HttpErrorResponse ? error.status : null;

  if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.ServiceUnavailable) return;

  authService.logout(error instanceof HttpErrorResponse ? error : undefined);
};

export const authMatchGuard: CanMatchFn = () => {
  if (isLoggedIn()) return true;

  logoutUnauthenticated(inject(AuthService));

  // Not `false`: an unmatched route falls through to the sibling `**`, which redirects back into this route
  return inject(Router).createUrlTree([RoutePath.Login]);
};

export const authGuard: CanActivateFn = (_, state) => {
  const authService = inject(AuthService);

  if (!isLoggedIn()) {
    logoutUnauthenticated(authService);

    return false;
  }

  const prefix = inject(AuthContextService).prefix;
  const router = inject(Router);

  return inject(AuthBootstrapService)
    .resolveAuthGuard()
    .pipe(
      map((resolve) => {
        if (!resolve) return false;

        return state.url.startsWith(prefix()) || router.createUrlTree([prefix() + RoutePath.NotFound]);
      }),
      catchError((error: unknown) => {
        handleBootstrapError(authService, error);

        return of(false);
      })
    );
};
